import { useState, useEffect } from 'react';
import { API_BASE_URL } from '@/lib/api-client';
import type { AvailableSlot } from '@/types/booking';

interface LinkedSlotsResponse {
  slot: AvailableSlot;
  linked_slots: AvailableSlot[];
  linked_count: number;
  message: string | null;
}

interface UseLinkedSlotsResult {
  linkedSlots: AvailableSlot[];
  linkedCount: number;
  message: string | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to fetch slots that would be blocked if a given slot is booked.
 * Used to show customers which other services will become unavailable.
 */
export function useLinkedSlots(slotId: number | null): UseLinkedSlotsResult {
  const [linkedSlots, setLinkedSlots] = useState<AvailableSlot[]>([]);
  const [linkedCount, setLinkedCount] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!slotId) {
      setLinkedSlots([]);
      setLinkedCount(0);
      setMessage(null);
      return;
    }

    const fetchLinkedSlots = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE_URL}/v1/available_slots/${slotId}/linked`);

        if (!response.ok) {
          throw new Error('Failed to fetch linked slots');
        }

        const result = await response.json();
        const data: LinkedSlotsResponse = result.data;

        setLinkedSlots(data.linked_slots || []);
        setLinkedCount(data.linked_count || 0);
        setMessage(data.message);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setLinkedSlots([]);
        setLinkedCount(0);
        setMessage(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLinkedSlots();
  }, [slotId]);

  return { linkedSlots, linkedCount, message, isLoading, error };
}
